import type {ReactElement, RefObject} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {SocialMediaWizardNav} from '@Components/social-media-wizard/components/social-media-wizard-nav';
import {SocialMediaWizardContent} from '@Components/social-media-wizard/components/social-media-wizard-content';
import {SocialAccountsList} from '@Components/social-media-wizard/components/social-accounts-list';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {
  getWizardContentSize,
  PUBLISH_PARAM_HANDLER_CLASS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import type {WizardPanelDescriptor} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialAccountConnection} from '@Components/social-media-wizard/components/social-account-connection';
import {SocialAccountButtons} from '@Components/social-media-wizard/components/social-account-buttons';
import {
  useAccountIdsToPublishTo,
  useAccountTypeToConnect,
  useHaveAllPostsPublished,
  useHaveAllPostsSettled,
  useNextButtonText,
  usePublishedSlotCounts,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useTriggerNext,
  useTriggerPrevious,
  useUpdatePublishingParams,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {getDateObjectForPrefilledDate} from '@Libraries/social-media-wizard-scheduling-library';
import {getKeyForPublishParams} from '@Libraries/social-media-wizard-design-library';
import {
  getNextButtonConfig,
  getNextWizardStep,
  getPublishingPanelHeading,
  getStartingURIForGallery,
} from '@Libraries/social-media-wizard-step-library';
import {
  disableNextButton,
  enableNextButton,
  initPublishingParams,
  resetAccountResizeStatusForDesignsMap,
  resetSocialPostCoverForAllPublishingParams,
  resetStateToDraft,
  showNextButton,
  syncPublishingParams,
  updateDesignsToPublish,
  updateDesignsToPublishAndTriggerNext,
  updateDesignSelectorTab,
  updateIsTemplateSelected,
  updateNextButtonIcon,
  updateNextButtonText,
  updatePost,
  updatePublishingAction,
  updateTriggerPrevious,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {SocialMediaPostDetails} from '@Components/social-media-wizard/components/social-media-post-details';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {
  usePostId,
  useDesignSelectorTab,
  useDesignsToPublish,
  useIsTemplateSelected,
  useSocialMediaWizardContainerQuery,
  useWizardCaptionErrors,
  useWizardConnectedAccounts,
  useWizardDesignsGraphicMetaMap,
  useWizardMobileNavView,
  useWizardPublishingParams,
  useWizardTitleErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {CircularProgressLoader} from '@Components/circular-progress-loader';
import {SocialMediaMobileFooter} from '@Components/social-media-wizard/components/social-media-mobile-footer';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {DesignSelection, SocialPlatform} from '@Components/social-media/social-media.types';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {SocialMediaWizardOnboarding} from '@Components/social-media-wizard/components/social-media-wizard-onboarding';
import {isUserLoggedIn} from '@Libraries/user.library';
import {DesignGallery} from '@Components/design-gallery';
import {TemplateEditorPanel} from '@Components/social-media-wizard/components/template-editor-panel';
import {WizardPreviewPanel} from '@Components/social-media-wizard/components/wizard-preview-panel/wizard-preview-panel';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {openErrorModal} from '@Modals/error-modal';
import {convertDateToUTCTimestamp} from '@/libraries/social-media-post.library';
import {CACHE_KEY_FOR_SCHEDULING} from '@Libraries/social-media-grid.library';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {
  goBackToEvent,
  goBackToEventFromOneClickPromotion,
  isEventOneClickPromotionFlow,
  isEventsSocialPostingFlow,
} from '@Libraries/social-media-event-promotion.library';
import {useAiCaptionWizardSetup, useDesignMetaSync, useEmbeddedEditorFlow, useWizardInit, useWizardNavigationHistory} from '@Components/social-media-wizard/social-media-wizard-feature.hooks';
import styles from './social-media-wizard.module.scss';
import {useAppDispatch} from '@/hooks';
import type {SocialMediaAccountId, SocialPost} from '../social-media/post.vo';
import {PostState} from '../social-media/post.vo';
import '@Global/modal';
import {WizardDesignActions} from '@Components/social-media-wizard/components/wizard-design-actions/wizard-design-actions';
import {WizardDesignSelectorPanel} from '@Components/social-media-wizard/components/wizard-design-selector-panel/wizard-design-selector-panel';

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 900,
  },
  large: {
    minWidth: 901,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

interface SocialMediaWizardProps {
  post?: SocialPost;
  accounts?: SocialAccount[];
  accountIdsForPost?: number[];
  design?: DesignSelection;
  template?: DesignSelection;
  isGalleryFlow?: boolean;
  eventHashId?: string;
  numParentAccountsConnected?: number;
  hasSeenOnboarding?: boolean;
  hasSeenConfetti?: boolean;
  hasUserPublishedBefore?: boolean;
  isSocialMediaWizardAiCaptionsEnabled?: boolean;
  isGoogleBusinessProfileEnabled?: boolean;
  emphasizedPlatform?: SocialPlatform;
}

function navigateOutOfWizard(): void {
  if (isEventsSocialPostingFlow()) {
    goBackToEvent();
    return;
  }

  if (isEventOneClickPromotionFlow()) {
    goBackToEventFromOneClickPromotion();
    return;
  }

  if (!document.referrer || window.PMW.isReferrer()) {
    window.location.href = window.PMW.util.site_url('posters/mine#/posts');
    return;
  }

  window.location.href = document.referrer;
}

export function SocialMediaWizard({
  post,
  accounts = [],
  accountIdsForPost = [],
  design,
  template,
  isGalleryFlow = false,
  eventHashId,
  numParentAccountsConnected = 0,
  hasSeenOnboarding = false,
  hasSeenConfetti = false,
  hasUserPublishedBefore = false,
  isSocialMediaWizardAiCaptionsEnabled = false,
  isGoogleBusinessProfileEnabled = false,
  emphasizedPlatform,
}: SocialMediaWizardProps): ReactElement<any> {
  const accountTypeToConnect = useAccountTypeToConnect();
  const wizardStep = useWizardStep();
  const triggerNext = useTriggerNext();
  const nextButtonText = useNextButtonText();
  const wizardPost = useSocialMediaWizardPost();
  const isTemplateSelected = useIsTemplateSelected();
  const isSelectTemplateFlow = !isUserLoggedIn() || isTemplateSelected;
  const navView = useWizardMobileNavView();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();
  const triggerPrevious = useTriggerPrevious();
  const dispatch = useAppDispatch();

  const {previousSteps, pushStep, updateWindowHistoryState, popPreviousStep} = useWizardNavigationHistory({
    wizardStep,
    post: post ?? null,
  });

  const [isLoadingEmbeddedEditor, setIsLoadingEmbeddedEditor] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [areAllPreviewsUpToDate, setAreAllPreviewsUpToDate] = useState(false);
  // Staged selection buffer (local); committed to Redux (useDesignsToPublish) on Next. Intentional split — do not collapse.
  const [designsToPublish, setDesignsToPublish] = useState<DesignSelection[]>([]);

  const {containerQueryParamProps, containerRef, hasContainerQueryInitialized} = useSocialMediaWizardContainerQuery();
  const myRef: RefObject<HTMLElement | null> = containerRef as RefObject<HTMLElement | null>;
  const params = useContainerQuery(myRef, containerQuery);
  const connectedAccounts = useWizardConnectedAccounts();
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(containerRef as RefObject<HTMLElement | null>);
  const designsToPublishTo = useDesignsToPublish();
  const postPublishingParams = useWizardPublishingParams();
  const designsGraphicsMetaMap = useWizardDesignsGraphicMetaMap();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const socialPost = useSocialMediaWizardPost();
  const postId = usePostId();
  const designSelectorTab = useDesignSelectorTab();
  const connectedAccountIds = connectedAccounts.map((account) => {
    return account.id;
  });
  const haveAllPostsPublished = useHaveAllPostsPublished();
  const haveAllPostsSettled = useHaveAllPostsSettled();
  const publishedSlotCounts = usePublishedSlotCounts();

  useAiCaptionWizardSetup({connectedAccountIds, postId});

  const {
    updatePublishingDesignsMeta,
    updateDesignsMetaOnClosingEmbeddedEditor,
    fetchGraphicsMetaForAnonymousTemplates,
    checkUpdateInGeneratedPreviewsStatus,
  } = useDesignMetaSync({designsToPublishTo, postId: postId ?? undefined, accountIdsToPublishTo});

  const {
    onClickEditDesign,
    onEmbeddedEditorOpened,
    onEmbeddedEditorForTemplateClosed,
    triggerEditDesignsFlow,
  } = useEmbeddedEditorFlow({
    designsToPublishTo,
    designsGraphicsMetaMap,
    design,
    updatePublishingDesignsMeta,
    updateDesignsMetaOnClosingEmbeddedEditor,
    setIsPreviewLoading,
    setIsLoadingEmbeddedEditor,
  });

  const setWizardStep = (step: SocialMediaWizardStep | null): void => {
    if (step) {
      dispatch(updateWizardStep(step));
    }
  };

  const setDetailsForSchedulingIfNeeded = (): void => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (!prefilledDate) {
      return;
    }

    const dateTime = getDateObjectForPrefilledDate(prefilledDate);
    const timeZoneOffset = dateTime.getTimezoneOffset();

    if (socialPost !== undefined) {
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };

      dispatch(updatePost(newPost));
    }
  };

  useDidUpdateEffect(() => {
    if (designsToPublishTo.length === 0 && post) {
      setDesignsToPublish(post.designs);
      dispatch(updateDesignsToPublish(post.designs));

      return;
    }

    if (isUserLoggedIn() && connectedAccountIds.length > 0) {
      void updatePublishingDesignsMeta();
    } else if (template || designsToPublishTo.length > 0) {
      void fetchGraphicsMetaForAnonymousTemplates(designsToPublishTo[0] ?? template);
    }
  }, [designsToPublishTo, connectedAccountIds.length]);


  const onPreviewLoaded = (): void => {
    setIsPreviewLoading(false);
  };

  const isMobileView = (): boolean => {
    return params.small || params.xsmall;
  };

  const getDesignActionsCtaSlot = (): ReactElement<any> | null => {
    if (isPreviewLoading || isSelectTemplateFlow) {
      return null;
    }

    return (
      <WizardDesignActions
        designsToPublishTo={designsToPublishTo}
        onDesignsStaged={setDesignsToPublish}
        onEditDesign={onClickEditDesign}
        onEmbeddedEditorOpened={onEmbeddedEditorOpened}
        isLoadingEmbeddedEditor={isLoadingEmbeddedEditor}
        setIsPreviewLoading={setIsPreviewLoading}
        setIsLoadingEmbeddedEditor={setIsLoadingEmbeddedEditor}
      />
    );
  };

  const linkSocialPostToEvent = (): void => {
    const idPost = wizardPost ? wizardPost.id : null;
    if (!eventHashId) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/linkSocialPostToEvent'), {
      idPost,
      eventHashId,
    });
  };

  const doUpdateNextButtonIfNeeded = (step: SocialMediaWizardStep): void => {
    const config = getNextButtonConfig({step, post: wizardPost, isSelectTemplateFlow});
    if (config.text !== nextButtonText) {
      dispatch(updateNextButtonText(config.text));
      dispatch(updateNextButtonIcon(config.icon));
    }
  };

  const onNext: VoidFunction = (): void => {
    initPanelSpecificHandlersOnNext();
    const nextStep = getNextWizardStep(post, accounts, wizardStep, {
      isGalleryFlow: isSelectTemplateFlow,
      emphasizedPlatform,
      template,
    });

    hideMobilePageSelectorPopup();

    if (nextStep) {
      initPanelSpecificHandlersOnCurrentStep(nextStep);

      const isAnyAccountErrorProne = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) !== null || getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null;

      if (isAnyAccountErrorProne && nextStep === SocialMediaWizardStep.PUBLISH) {
        return;
      }

      setWizardStep(nextStep);
      setDetailsForSchedulingIfNeeded();
    }
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId | null): void => {
    if (!accountId) {
      return;
    }

    const publishParamHandlerClass = PUBLISH_PARAM_HANDLER_CLASS;
    const yOffset = -100;
    const element = document.querySelectorAll(`.${publishParamHandlerClass}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];
    const y = element.getBoundingClientRect().top + yOffset + window.scrollY;

    window.scrollTo({top: y, behavior: 'smooth'});
  };

  const initPanelSpecificHandlersOnCurrentStep = (currentStep: SocialMediaWizardStep): void => {
    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (currentStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
      scrollToAccountSection(captionErrorAccount ?? titleErrorAccount);
      return;
    }

    if (currentStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: wizardPost?.id,
        })
      );
    }

    if (currentStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }
  };

  const doToggleNextButtonVisibility = (): void => {
    dispatch(showNextButton());
  };

  const hideMobilePageSelectorPopup = (): void => {
    document.querySelector('.mobile-pages-view')?.classList.add('_hidden');
  };

  const initPanelSpecificHandlersOnNext = (): void => {
    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      const designs = designsToPublish;
      dispatch(showNextButton());

      if (designs.length > 0) {
        // Commit the staged selection buffer to Redux (the single committed source consumed downstream).
        dispatch(updateDesignsToPublish(designs));
      }
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      dispatch(resetAccountResizeStatusForDesignsMap());
    }
  };

  const resetSocialPostCoverInPublishingParamsIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    const isPreviouslySelectedPostSingleGraphic = designsToPublishTo.length === 1;
    const shouldResetCoverForReselectedDesign = newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isPreviouslySelectedPostSingleGraphic;

    if (shouldResetCoverForReselectedDesign) {
      dispatch(resetSocialPostCoverForAllPublishingParams());
    }
  };

  const cleanupTemplateFlowIfNeeded = (newWizardStep: SocialMediaWizardStep): void => {
    if (newWizardStep === SocialMediaWizardStep.SELECT_DESIGN && isUserLoggedIn() && isTemplateSelected) {
      setDesignsToPublish([]);
      dispatch(updateDesignsToPublish([]));
      dispatch(updateIsTemplateSelected(false));
    }
  };

  /**
   * The publish / save / schedule screen (PublishingPost) renders whenever a publishing action is set — see
   * SocialMediaPostPublishParams — independent of the wizard step, which can drift out of sync with it. Returning
   * from that screen (back button, "Continue editing", "Edit & reschedule") always resumes detail editing.
   */
  const isReturningFromPublishScreen = (): boolean => {
    return publishingAction !== undefined;
  };

  const getStepToReturnTo = (poppedStep: SocialMediaWizardStep | undefined): SocialMediaWizardStep | undefined => {
    if (isReturningFromPublishScreen()) {
      return SocialMediaWizardStep.ADD_DETAILS;
    }

    return poppedStep;
  };

  /**
   * Clears the publishing action in the same render as the step change so the caption inputs reappear and we never
   * leave the post on a stale publish screen (or flash the "Publishing..." header) while going back to editing.
   */
  const resetPublishingStateWhenLeavingPublishScreen = (): void => {
    if (isReturningFromPublishScreen()) {
      dispatch(resetStateToDraft());
    }
  };

  const onBack: VoidFunction = (): void => {
    const targetStep: SocialMediaWizardStep | undefined = getStepToReturnTo(popPreviousStep());

    if (targetStep) {
      resetPublishingStateWhenLeavingPublishScreen();
      resetSocialPostCoverInPublishingParamsIfNeeded(targetStep);
      cleanupTemplateFlowIfNeeded(targetStep);
      setWizardStep(targetStep);
    } else {
      navigateOutOfWizard();
    }
  };

  const onDesignListChange = (designs: DesignSelection[]): void => {
    setDesignsToPublish(designs);
  };

  const onGalleryDesignsSelected = (templateDesigns: DesignSelection[]): void => {
    dispatch(updateIsTemplateSelected(true));
    setDesignsToPublish(templateDesigns);
    dispatch(updateDesignsToPublishAndTriggerNext(templateDesigns));
  };

  const getDesignSelectorTab = (): DesignSelectorTab => {
    if (isGalleryFlow) {
      return DesignSelectorTab.GALLERY_TEMPLATES;
    }

    return DesignSelectorTab.MY_DESIGNS;
  };

  const onCancel = (): void => {
    dispatch(updateTriggerPrevious());
  };

  const getMainContentForConnectAccountsPanel = (): ReactElement<any> => {
    const numAccountsConnected = connectedAccounts.length;

    if (numAccountsConnected === 0) {
      return <SocialAccountButtons />;
    }

    return (
      <>
        <div className="spacing-m-b-4 flex-row-align-center">
          <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_20} type={IconType.GHOST} shape={IconShape.SQUARE} className="spacing-m-r-2" onClick={onCancel} />
          <Text size={TextSize.SMALL} className="spacing-m-l-2" val={window.i18next.t('pmwjs_add_another_account')} bold />
        </div>
        <SocialAccountButtons />
      </>
    );
  };

  const onClickAddAccount = (): void => {
    setWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS);
  };

  const getSocialWizardPublishingPanelHeading = (): string => {
    return getPublishingPanelHeading({publishingAction, haveAllPostsPublished, haveAllPostsSettled, publishedSlotCounts});
  };

  const defaultPreviewContent: ReactElement<any> = (
    <WizardPreviewPanel
      wizardStep={wizardStep}
      designsToPublishTo={designsToPublishTo}
      postPublishingParams={postPublishingParams}
      designsGraphicsMetaMap={designsGraphicsMetaMap}
      isSelectTemplateFlow={isSelectTemplateFlow}
      isMobileView={isMobileView()}
      isPreviewLoading={isPreviewLoading}
      areAllPreviewsUpToDate={areAllPreviewsUpToDate}
      ctaSlot={getDesignActionsCtaSlot()}
      onOrphanedPreviewClick={triggerEditDesignsFlow}
      onPreviewLoaded={onPreviewLoaded}
    />
  );

  const getFallbackPanelDescriptor = (): WizardPanelDescriptor => {
    return {
      mainContent: <SocialAccountButtons />,
      header: '',
      previewContent: defaultPreviewContent,
      isFluidContent: false,
    };
  };

  const getWizardPanelDescriptor = (): WizardPanelDescriptor => {
    if (!wizardStep) {
      return getFallbackPanelDescriptor();
    }

    switch (wizardStep) {
      case SocialMediaWizardStep.SELECT_DESIGN: {
        const selectDesignMainContent = isSelectTemplateFlow && !isUserLoggedIn()
          ? <DesignGallery onDesignsSelected={onGalleryDesignsSelected} height="83.5vh" startingPageURI={getStartingURIForGallery()} />
          : (
            <WizardDesignSelectorPanel
              designsToPublish={designsToPublish}
              onDesignListChange={onDesignListChange}
              onDesignsStaged={setDesignsToPublish}
              designSelectorTab={designSelectorTab}
              isMobileView={isMobileView()}
            />
          );
        return {
          mainContent: selectDesignMainContent,
          header: window.i18next.t('pmwjs_start_by_selecting_design'),
          previewContent: undefined,
          isFluidContent: true,
        };
      }

      case SocialMediaWizardStep.CONNECT_ACCOUNTS:
        return {
          mainContent: getMainContentForConnectAccountsPanel(),
          header: window.i18next.t('pmwjs_where_to_publish'),
          previewContent: defaultPreviewContent,
          isFluidContent: false,
        };

      case SocialMediaWizardStep.ACCOUNT_CONNECTION: {
        const accountConnectionMainContent = accountTypeToConnect
          ? <SocialAccountConnection accountTypeToConnect={accountTypeToConnect} />
          : <SocialAccountButtons />;
        return {
          mainContent: accountConnectionMainContent,
          header: accountTypeToConnect ? window.i18next.t('pmwjs_where_to_publish') : '',
          previewContent: defaultPreviewContent,
          isFluidContent: false,
        };
      }

      case SocialMediaWizardStep.SELECT_ACCOUNTS:
        return {
          mainContent: <SocialAccountsList />,
          header: window.i18next.t('pmwjs_one_or_more_accounts'),
          previewContent: defaultPreviewContent,
          isFluidContent: false,
        };

      case SocialMediaWizardStep.EDIT_TEMPLATE:
        return {
          mainContent: (
            <TemplateEditorPanel
              onSave={(posterHashId) => {
                void onEmbeddedEditorForTemplateClosed(posterHashId);
              }}
              onClose={onCancel}
            />
          ),
          header: '',
          previewContent: undefined,
          isFluidContent: false,
        };

      case SocialMediaWizardStep.ADD_DETAILS: {
        const addDetailsHeader = accountIdsToPublishTo.length > 1
          ? window.i18next.t('pmwjs_add_details_to_your_posts_x', {x: accountIdsToPublishTo.length})
          : window.i18next.t('pmwjs_add_details_to_your_post');
        return {
          mainContent: <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'details-panel'} />,
          header: addDetailsHeader,
          previewContent: undefined,
          isFluidContent: false,
        };
      }

      case SocialMediaWizardStep.PUBLISH: {
        if (accountIdsToPublishTo.length > 0) {
          return {
            mainContent: <SocialMediaPostDetails onClickAddAccount={onClickAddAccount} size={getWizardContentSize(params)} key={'publishing-panel'} />,
            header: getSocialWizardPublishingPanelHeading(),
            previewContent: undefined,
            isFluidContent: false,
          };
        }
        return getFallbackPanelDescriptor();
      }

      default:
        return getFallbackPanelDescriptor();
    }
  };

  useUpdatePublishingParams();

  useDidUpdateEffect(() => {
    pushStep();
    doUpdateNextButtonIfNeeded(wizardStep);
  }, [wizardStep]);

  useEffect(() => {
    doToggleNextButtonVisibility();

    if (wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
      dispatch(updateDesignSelectorTab(getDesignSelectorTab()));

      if (designsToPublish.length === 0) {
        dispatch(disableNextButton());
      }

      setDesignsToPublish(designsToPublishTo);
    }

    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS) {
      setIsPreviewLoading(true);
      if (isUserLoggedIn()) {
        void checkUpdateInGeneratedPreviewsStatus(designsToPublishTo).then((allGenerated) => {
          if (allGenerated) {
            setAreAllPreviewsUpToDate(true);
          }
        });
      }
    }

    // codeReviewAhsan: review this check if needed
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (isEventsSocialPostingFlow()) {
        linkSocialPostToEvent();
      }
    }

    updateWindowHistoryState(wizardStep);
  }, [wizardStep]);

  const toggleNextButtonByAccountsConnected = (): void => {
    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }
  };

  useEffect(() => {
    toggleNextButtonByAccountsConnected();
  }, [connectedAccounts]);

  useWizardInit({
    post,
    accounts,
    accountIdsForPost,
    design,
    template,
    isGalleryFlow,
    isSelectTemplateFlow,
    emphasizedPlatform,
    hasSeenOnboarding,
    hasSeenConfetti,
    hasUserPublishedBefore,
    isSocialMediaWizardAiCaptionsEnabled,
    isGoogleBusinessProfileEnabled,
    numParentAccountsConnected,
    setDesignsToPublish,
  });

  useDidUpdateEffect(() => {
    onNext();
  }, [triggerNext]);

  useDidUpdateEffect(() => {
    onBack();
  }, [triggerPrevious]);

  if (wizardStep === SocialMediaWizardStep.CONNECT_ACCOUNTS) {
    dispatch(disableNextButton());
  }

  if (wizardStep === SocialMediaWizardStep.ACCOUNT_CONNECTION && !accountTypeToConnect) {
    openErrorModal({message: window.i18next.t('pmwjs_error_message')});
  }

  const {mainContent, header, previewContent, isFluidContent} = getWizardPanelDescriptor();

  const getNavSteps = (): string[] => {
    const steps: string[] = [];

    if (!post && !template) {
      steps.push(window.i18next.t('pmwjs_design'));
    }

    steps.push(window.i18next.t('pmwjs_accounts'));

    if (isSelectTemplateFlow) {
      steps.push(window.i18next.t('pmwjs_edit'));
    }

    steps.push(window.i18next.t('pmwjs_publish'));

    return steps;
  };

  const getFooter = (): ReactElement<any> | null => {
    const size = getWizardContentSize(params);
    const numberSteps = getNavSteps().length;

    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      return null;
    }

    if (size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL) {
      return (
        <SocialMediaMobileFooter
          shouldShowShadow={bottomShadow && !isLoadingPreview()}
          onNext={onNext}
          onPrevious={onBack}
          nSteps={numberSteps}
          currentStep={previousSteps.length + 1}
          currentSelectedDesigns={designsToPublish}
        />
      );
    }

    return null;
  };

  const getContainerShadowClasses = (): string => {
    if (topShadow) {
      return styles.topShadow;
    }

    return '';
  };

  const isLoadingPreview = (): boolean => {
    return wizardStep !== SocialMediaWizardStep.PUBLISH && wizardStep !== SocialMediaWizardStep.ADD_DETAILS && navView === WizardMobileNavView.PREVIEW;
  };

  const getContainerClasses = (): string => {
    const size = getWizardContentSize(params);
    const isMobile = size === SocialMediaWizardContentSize.XSMALL || size === SocialMediaWizardContentSize.SMALL;
    const isLoading = isLoadingPreview();
    const numberAccountsToPublishTo = accountIdsToPublishTo.length;

    if (isMobile) {
      return `${!isLoading && numberAccountsToPublishTo > 1 ? styles.overflowClasses : '_full-height'} ${getContainerShadowClasses()} _full-width spacing-m-b-10`;
    }

    return `_full-width`;
  };

  const socialMediaWizardContainerQueriesContextValues = useMemo(() => {
    return {
      isExtraSmallScreen: containerQueryParamProps.isExtraSmallScreen,
      isSmallScreen: containerQueryParamProps.isSmallScreen,
      isMediumScreen: containerQueryParamProps.isMediumScreen,
      isLargeScreen: containerQueryParamProps.isLargeScreen,
      isExtraLargeScreen: containerQueryParamProps.isExtraLargeScreen,
    };
  }, [containerQueryParamProps]);

  return (
    <>
      <SocialMediaWizardNav currentSelectedDesigns={designsToPublish} navSteps={getNavSteps()} onNext={onNext} onBack={onBack} />
      <SocialMediaWizardContainerQueriesContext.Provider value={socialMediaWizardContainerQueriesContextValues}>
        <div ref={containerRef} className={getContainerClasses()}>
          {hasContainerQueryInitialized ? (
            <>
              <SocialMediaWizardContent
                size={getWizardContentSize(params)}
                heading={header}
                mainContent={mainContent}
                previewContent={previewContent}
                fluidWidth={isFluidContent}
              />
              <SocialMediaWizardOnboarding />
            </>
          ) : (
            <CircularProgressLoader classes="-for-mystuff-content" />
          )}
        </div>
      </SocialMediaWizardContainerQueriesContext.Provider>
      {getFooter()}
    </>
  );
}
